import { parse } from 'papaparse';

interface Condition {
  a: string;
  b: string;
  c: string;
}

interface CmsCase {
  element: HTMLElement;
  conditions: Condition[];
}

interface Address {
  zip: string;
  city: string;
  street: string;
  houseNumber: string;
  colAValue: string;
  colBValue: string;
  colCValue: string;
}

interface Field {
  input: HTMLInputElement;
  dataList: HTMLDataListElement;
}

/** Selector for any instance of this _Friendation_ component */
const selector = '[is="block-fibre-checker"]';

/** Data for the fibreChecker */
const addresses: Address[] = [];

/**
 * Friendation component
 */
export const blockFibreChecker = () => {
  /** Instances of this Friendation component */
  const components: HTMLElement[] = Array.from(
    document.querySelectorAll(selector)
  );

  components.forEach(async (component) => {
    /** The form containing all {@link fields} */
    const form = component.querySelector('form');

    const submitButton: HTMLButtonElement =
      form.querySelector('[type="submit"]');

    /** Fields in {@link form}. We query the fields in {@link handleForm} */
    const fields: {
      zip: Field;
      street: Field;
      houseNumber: Field;
    } = {
      zip: { input: null, dataList: null },
      street: { input: null, dataList: null },
      houseNumber: { input: null, dataList: null }
    };

    /**
     * CSV column names mapped in the CMS via dropdowns, passed through
     * `data-col-*` attributes. Lower-cased so they match the lower-cased
     * header lookup ({@link headerMap}); `houseNumber2` and `colC` are optional.
     */
    const readColName = (raw: string | undefined) =>
      (raw ?? '').trim().toLowerCase();

    const cols = {
      street: readColName(component.dataset.colStreet),
      houseNumber: readColName(component.dataset.colHouseNumber),
      houseNumber2: readColName(component.dataset.colHouseNumber2),
      zip: readColName(component.dataset.colZip),
      city: readColName(component.dataset.colCity),
      colA: readColName(component.dataset.colA),
      colB: readColName(component.dataset.colB),
      colC: readColName(component.dataset.colC)
    };

    /** Case elements rendered from CMS configuration */
    const cmsCaseElements = Array.from(
      component.querySelectorAll<HTMLElement>(
        '.fibre-case:not(.fibre-case--not-found)'
      )
    );
    const notFoundElement = component.querySelector<HTMLElement>(
      '.fibre-case--not-found'
    );

    /** CMS-configured cases with their parsed conditions */
    const cmsCases: CmsCase[] = cmsCaseElements.map((element) => ({
      element,
      conditions: Array.from(
        element.querySelectorAll<HTMLElement>('.fibre-condition')
      ).map((span) => ({
        a: span.dataset.a ?? '',
        b: span.dataset.b ?? '',
        c: span.dataset.c ?? ''
      }))
    }));

    /** Populate the zip code field with matching zip codes */
    const populateZipCodes = () => {
      fields.zip.dataList.innerHTML = '';

      addresses
        .sort((a, b) => a.zip.localeCompare(b.zip))
        .forEach((address) => {
          const value = `${address.zip}, ${address.city}`;

          if (
            Array.from(fields.zip.dataList.options).find(
              (x) => x.value === value
            )
          )
            return;

          const option = document.createElement('option');
          option.value = value;
          fields.zip.dataList.append(option);
        });
    };

    const populateStreets = (zipCode: Address['zip']) => {
      fields.street.dataList.innerHTML = '';

      addresses
        .sort((a, b) => a.street.localeCompare(b.street))
        .filter((x) => x.zip === zipCode)
        .forEach((address) => {
          if (
            Array.from(fields.street.dataList.options).find(
              (x) => x.value === address.street
            )
          )
            return;

          const option = document.createElement('option');
          option.value = address.street;
          fields.street.dataList.append(option);
        });
    };

    const populateHouseNumbers = (street: Address['street']) => {
      fields.houseNumber.dataList.innerHTML = '';

      addresses
        .sort((a, b) => parseInt(a.houseNumber) - parseInt(b.houseNumber))
        .filter((x) => x.street === street)
        .forEach((address) => {
          if (
            Array.from(fields.houseNumber.dataList.options).find(
              (x) => x.value === address.houseNumber
            )
          )
            return;

          const option = document.createElement('option');
          option.value = address.houseNumber;
          fields.houseNumber.dataList.append(option);
        });
    };

    const onInputZip = () => {
      const value = fields.zip.input.value;

      const match = addresses.find(
        (x) =>
          `${x.zip}, ${x.city}`.toLowerCase() ===
          fields.zip.input.value.toLowerCase()
      );

      if (value.length && match) {
        fields.street.input.disabled = false;
        populateStreets(match.zip);
      } else {
        fields.street.input.value = '';
        fields.street.input.disabled = true;
        fields.houseNumber.input.value = '';
        fields.houseNumber.input.disabled = true;
      }
    };

    const onInputStreet = () => {
      const value = fields.street.input.value;

      const match = addresses.find(
        (x) =>
          x.street.toLowerCase() === fields.street.input.value.toLowerCase()
      );

      fields.houseNumber.input.disabled = !(value.length > 2);

      if (value.length > 2 && match) {
        populateHouseNumbers(match.street);
      } else {
        fields.houseNumber.input.value = '';
      }
    };

    const onInputHouseNumber = () => {
      submitButton.disabled = !fields.houseNumber.input.value.length;
    };

    const handleForm = () => {
      fields.zip.input = form.querySelector('input[name="zipCode"]');
      fields.zip.dataList = form.querySelector(
        `[id="${fields.zip.input.getAttribute('list')}"]`
      );
      fields.street.input = form.querySelector('input[name="street"]');
      fields.street.dataList = form.querySelector(
        `[id="${fields.street.input.getAttribute('list')}"]`
      );
      fields.houseNumber.input = form.querySelector(
        'input[name="houseNumber"]'
      );
      fields.houseNumber.dataList = form.querySelector(
        `[id="${fields.houseNumber.input.getAttribute('list')}"]`
      );

      fields.zip.input.addEventListener('input', onInputZip);
      fields.street.input.addEventListener('input', onInputStreet);
      fields.houseNumber.input.addEventListener('input', onInputHouseNumber);

      populateZipCodes();

      form.addEventListener('submit', (e: SubmitEvent) => {
        e.preventDefault();
        submitData();
      });

      form.addEventListener('reset', () => {
        const allCaseElements = [...cmsCaseElements, notFoundElement].filter(
          Boolean
        );
        allCaseElements.forEach((el) => {
          el.inert = true;
        });
        fields.street.input.disabled = true;
        fields.houseNumber.input.disabled = true;
        submitButton.disabled = true;
        fields.zip.input.focus();
      });
    };

    /**
     * Read and parse the data of a CSV file. The CSV contains information
     * about the availability of a fibre optic connection at an address.
     */
    const readData = async () => {
      if (addresses.length) return;

      const linkToCsv = component.dataset.url;
      if (!linkToCsv)
        throw new Error(
          'Missing dataset attribute on component block-fibre-checker'
        );
      component.removeAttribute('data-url');

      const charset = 'utf-8';
      const myHeaders = new Headers();
      myHeaders.append('Content-Type', `text/plain; charset=${charset}`);

      const csv = await fetch(linkToCsv, { headers: myHeaders })
        .then((x) => x.arrayBuffer())
        .then((x) => new TextDecoder(charset).decode(x));

      const csvData = parse(csv, {
        delimiter: ';',
        skipEmptyLines: true
      });

      const rows = csvData.data as string[][];

      if (!rows.length) {
        console.error('blockFibreChecker: CSV ist leer');
        return;
      }

      /** Build a map of lowercase column name → index from the header row */
      const headerRow = rows[0];
      const headerMap: Record<string, number> = Object.fromEntries(
        headerRow.map((col, i) => [col.trim().toLowerCase(), i])
      );

      const validateCol = (name: string) => {
        if (headerMap[name] === undefined)
          console.error(
            `blockFibreChecker: Spalte "${name}" nicht im CSV gefunden`
          );
      };

      /** Required mappings — enforced in the CMS, validated here as a net */
      [
        cols.street,
        cols.houseNumber,
        cols.zip,
        cols.city,
        cols.colA,
        cols.colB
      ].forEach(validateCol);

      // houseNumber2 and colC are optional: only validate when a column was mapped
      if (cols.houseNumber2) validateCol(cols.houseNumber2);
      if (cols.colC) validateCol(cols.colC);

      const idx = {
        street: headerMap[cols.street],
        houseNumber: headerMap[cols.houseNumber],
        houseNumber2: cols.houseNumber2
          ? headerMap[cols.houseNumber2]
          : undefined,
        zip: headerMap[cols.zip],
        city: headerMap[cols.city],
        colA: headerMap[cols.colA],
        colB: headerMap[cols.colB],
        colC: cols.colC ? headerMap[cols.colC] : undefined
      };

      const invalidRows: Array<{ row: string[]; i: number }> = [];

      const checkValidity = (row: string[], i: number): boolean => {
        let valid = true;

        if (
          idx.zip === undefined ||
          !row[idx.zip] ||
          Number.isNaN(parseInt(row[idx.zip], 10))
        )
          valid = false;
        if (idx.city === undefined || !row[idx.city]) valid = false;
        if (idx.street === undefined || !row[idx.street]) valid = false;
        if (idx.houseNumber === undefined || !row[idx.houseNumber])
          valid = false;

        if (!valid) invalidRows.push({ row, i });
        return valid;
      };

      rows.slice(1).forEach((row, i) => {
        if (!checkValidity(row, i + 1)) return;

        const zip = row[idx.zip].trim();
        const city = row[idx.city].trim();
        const street = row[idx.street].trim();
        const houseNumber = `${row[idx.houseNumber].trim()}${(row[idx.houseNumber2] ?? '').trim()}`;
        const colAValue =
          idx.colA !== undefined ? (row[idx.colA] ?? '').trim() : '';
        const colBValue =
          idx.colB !== undefined ? (row[idx.colB] ?? '').trim() : '';
        const colCValue =
          idx.colC !== undefined ? (row[idx.colC] ?? '').trim() : '';

        if (zip.length && city.length && street.length && houseNumber.length) {
          addresses.push({
            zip,
            city,
            street,
            houseNumber,
            colAValue,
            colBValue,
            colCValue
          });
        }
      });

      if (invalidRows.length)
        console.warn(
          `blockFibreChecker: ${invalidRows.length} / ${rows.length} Zeilen sind ungültig`,
          invalidRows
        );
    };

    const normalize = (s: string) => s.trim().toUpperCase();

    /** Treats the admin-entered keyword "leer" as an empty string */
    const resolveValue = (v: string) =>
      normalize(v) === 'LEER' ? '' : normalize(v);

    const matchesCase = (address: Address, c: CmsCase): boolean =>
      c.conditions.some((cond) => {
        const aMatches =
          resolveValue(address.colAValue) === resolveValue(cond.a);
        const bMatches =
          resolveValue(address.colBValue) === resolveValue(cond.b);

        // Column C is an optional extra AND-constraint. It is only evaluated
        // when the admin mapped a column C *and* this condition defines a
        // value for it; otherwise C is ignored (backwards compatible).
        // Note: an empty value means "ignore", whereas the keyword "leer"
        // (handled by resolveValue) means "column C must be empty".
        const cIgnored = !cols.colC || cond.c.trim() === '';
        const cMatches =
          cIgnored || resolveValue(address.colCValue) === resolveValue(cond.c);

        return aMatches && bMatches && cMatches;
      });

    /**
     * When data is submitted, find the first matching CMS case and show its
     * message. Falls back to the not-found message when no case matches.
     */
    const submitData = () => {
      const match = addresses.find(
        (x) =>
          `${x.zip}, ${x.city}`.toLowerCase() ===
            fields.zip.input.value.toLowerCase().trim() &&
          x.street.toLowerCase() ===
            fields.street.input.value.toLowerCase().trim() &&
          x.houseNumber.toLowerCase() ===
            fields.houseNumber.input.value.toLowerCase().trim()
      );

      const allCaseElements = [...cmsCaseElements, notFoundElement].filter(
        Boolean
      );
      allCaseElements.forEach((el) => {
        el.inert = true;
      });

      const chosenElement =
        match !== undefined
          ? (cmsCases.find((c) => matchesCase(match, c))?.element ??
            notFoundElement)
          : notFoundElement;

      if (chosenElement) {
        chosenElement.inert = false;
        const message = chosenElement.querySelector<HTMLElement>('.message');
        if (message) {
          message.tabIndex = -1;
          setTimeout(() => {
            message.focus();
          }, 100);
        }
      }
    };

    readData().then(() => {
      handleForm();
    });
  });
};
